/**
 * The tunables the *guns* run on, split out of `tunables.ts` for the same
 * reason `ai-tunables.ts` was: the register for how an aeroplane flies should
 * not have to be read past to find how a round behaves. They merge straight
 * back into `DEFAULT_TUNABLES`, so nothing downstream sees more than one table
 * — `Tunables` is still one flat record and `makeTunables` still overrides any
 * key by name.
 *
 * What is in here: rate of fire and ammunition, the jam model
 * (flight-tunables.md §6 — one global jam threshold of 200 on all 33 aircraft,
 * with Mobley's measured "≈300 rounds / 5–6 s" response behind it), the
 * ballistics AD-007g's gunnery section describes, and what a hit *does* to the
 * pilot and the airframe.
 *
 * Five keys carry real provenance. `gunJamThresholdReference` and
 * `gunEffectivenessReference` are value classes read straight off the data;
 * `gunIncendiaryJitterBam`, `gunAoaPitchFraction` and `gunTracerInterval` are
 * RECOVERED from `RB.EXE`'s own firing code (AD-007g's 2026-08-19 amendment).
 * Everything else is CALIBRATION-PENDING in flight-tunables.md §8's sense: the
 * `.DAT` stores no muzzle velocity, no damage-per-round and no range law, so
 * those were set by measuring outcome distributions over scripted duels
 * (docs/engineering/lessons.md, combat lane), not transcribed.
 */

export const GUNNERY_TUNABLES = {
  /** Rounds per second per gun. CALIBRATION-PENDING; 55 reconciles Mobley's two figures for the shipped jam setting — "~300 rounds" and "5–6 s of fire" (flight-tunables.md §6). */
  gunRoundsPerSecond: 55,
  /** Rounds carried per gun position. CALIBRATION-PENDING — the data has no ammo-capacity field. */
  gunRoundsPerGun: 500,
  /** Per-round jam probability at the start of a burst, at the fleet-standard jam threshold. CALIBRATION-PENDING; tuned so continuous fire jams near 300 rounds. */
  gunJamBaseProbability: 0.0022,
  /** Burst length over which the per-round jam probability doubles — the manual's "long burst has a greater chance of jamming". CALIBRATION-PENDING. */
  gunJamBurstScaleRounds: 300,
  /** Jam-threshold value treated as unity — 200 on all 33 aircraft (flight-tunables.md §6). VERIFIED. */
  gunJamThresholdReference: 200,
  /** Chance one U-key press clears a jam. CALIBRATION-PENDING. */
  gunClearProbability: 0.35,
  /** Failed clear attempts before a jam can become permanent (manual: "it is possible for your machine guns to jam permanently"). CALIBRATION-PENDING. */
  gunPermanentJamAttempts: 4,
  /** Chance a failed clear past that point turns the jam permanent. CALIBRATION-PENDING. */
  gunPermanentJamProbability: 0.2,

  /** Muzzle velocity of the era's rifle-calibre guns (Vickers .303 ≈ 744, Spandau 7.92 ≈ 895 m/s). CALIBRATION-PENDING — the game stores no ballistic field at all. */
  gunMuzzleSpeedMps: 800,
  /** Harmonization range the gun lines cross the boresight at. CALIBRATION-PENDING; period practice was 100–200 yd. */
  gunConvergenceM: 150,
  /** Lateral offset of a muzzle from the centreline — the residual aim error away from convergence. CALIBRATION-PENDING; the `.DAT` tracer-origin fields are per-aircraft but not transcribed into the fleet table yet. */
  gunMuzzleOffsetM: 0.32,
  /** Gun-effectiveness value (offset 206) treated as unity — the 1024 class. VERIFIED as a value class (flight-tunables.md §6). */
  gunEffectivenessReference: 1024,
  /** Component health one round removes at the reference effectiveness and point-blank range. CALIBRATION-PENDING. */
  gunDamagePerRound: 0.055,
  /** Range at which a round does half its point-blank damage. CALIBRATION-PENDING. */
  gunDamageHalfRangeM: 220,
  /** Range past which a round is treated as spent and cannot hit. CALIBRATION-PENDING. */
  gunMaxRangeM: 700,
  /**
   * The one-sided jitter the original adds to a round's pitch and yaw at spawn,
   * BAM16, and **only while the shooter's airframe is damaged**: `0…63` counts,
   * i.e. 0…0.346°, drawn uniformly and added (never subtracted) on each axis.
   * RECOVERED from `RB.EXE`'s `mg.c` — the *only* randomness anywhere in the
   * original's flight path of a round.
   */
  gunIncendiaryJitterBam: 63,
  /**
   * Fraction of angle of attack the original adds to a round's pitch at spawn.
   * RECOVERED as 0.75 from `mg.c`. Applied in the original against an attitude
   * whose pitch *is* the flight path, so in this port — where the boresight is
   * the fuselage axis and the velocity vector genuinely sits α below it — the
   * round leaves `(1 − 0.75)·α` **under** the boresight. See `gunnery.ts`'s
   * `aoaPitchOffsetRad` for why that sign is the coherent reading of the two.
   */
  gunAoaPitchFraction: 0.75,
  /**
   * Half-width of the uniform multiplier on a hit's damage — the original rolls
   * for how much a round takes off *after* the geometry has already decided it
   * connected, and never for whether it connected. Symmetric about 1, so the
   * mean damage per hit is exactly `gunDamagePerRound`. CALIBRATION-PENDING;
   * the original's own spread is not recovered.
   */
  gunDamageRandomSpread: 0.5,
  /**
   * Belt loading: one round in this many is a tracer, and a tracer is the only
   * round anybody can see. RECOVERED as 1-in-3. Every round still flies and
   * still collides — this decides what is *drawn*, not what is fired.
   */
  gunTracerInterval: 3,
  /** Target radius in metres per unit of the `.DAT` target-size field (offset 140): 120 → 4.2 m, the heavies' 300 → 10.5 m. CALIBRATION-PENDING. */
  targetRadiusMetresPerUnit: 0.035,

  /** Chance a round that lands on the pilot kills rather than wounds. CALIBRATION-PENDING. */
  pilotHitLethalProbability: 0.25,
  /** Chance an engine hit starts a fire (docs/game/flight-model.md §7: "the engine can overheat and catch fire"). CALIBRATION-PENDING. */
  engineFireProbabilityPerHit: 0.09,
  /** Chance a fuel-tank hit starts a fire. CALIBRATION-PENDING — no source isolates a fuel-hit effect at all (§7). */
  fuelFireProbabilityPerHit: 0.13,
  /** Engine health lost per second while the aircraft burns. CALIBRATION-PENDING. */
  fireEngineDamagePerSecond: 0.06,
  /** Wing health lost per second while the aircraft burns — what turns a fire into a burning descent. CALIBRATION-PENDING. */
  fireStructureDamagePerSecond: 0.035,
  /** Engine health below which the engine sputters audibly. CALIBRATION-PENDING. */
  engineSputterHealth: 0.6,
} satisfies Record<string, number>;

export type GunneryTunableKey = keyof typeof GUNNERY_TUNABLES;

/**
 * The keys above whose values are *not* read off the data or recovered from
 * `RB.EXE`, for `CALIBRATION_PENDING_TUNABLES` to spread in — kept derived
 * rather than hand-listed so adding a key here cannot silently escape the work
 * queue.
 */
const SOURCED_GUNNERY_TUNABLES: readonly GunneryTunableKey[] = [
  "gunJamThresholdReference",
  "gunEffectivenessReference",
  "gunIncendiaryJitterBam",
  "gunAoaPitchFraction",
  "gunTracerInterval",
];

export const GUNNERY_TUNABLE_KEYS: readonly GunneryTunableKey[] = (
  Object.keys(GUNNERY_TUNABLES) as GunneryTunableKey[]
).filter((key) => !SOURCED_GUNNERY_TUNABLES.includes(key));
